// =====================================================================
// useCotizadorManual.js
// Estado + cálculos + generación de los mensajes de WhatsApp tal como
// los produce el Excel (mensaje 1: precios totales, mensaje 2: "ES
// DECIR..." por persona + texto de "Te incluye...").
// =====================================================================

import { ref, computed, watch } from "vue"
import * as calc from "@/utils/calculos"
import {
  proper,
  dollar,
  rangoFechas,
  REVISE_FECHAS
} from "@/utils/excelFormulas"
import {
  getDestinoManual,
  destinosManual
} from "@/utils/destinosManualConfig"
import { NOTA_PRECIO_TOTAL } from "@/utils/textosIncluye"
import { cargarParametros } from "@/utils/parametrosManual"

const nuevoHotel = () => ({ nombre: "", valor: 0 })

export function useCotizadorManual() {
  // Trae de la BD los parámetros vigentes de las fórmulas (si cambian,
  // los computeds recalculan solos porque `parametros` es reactivo).
  cargarParametros(true)

  // ------------------------------- STATE -------------------------------
  const destinoManual = ref(destinosManual[0].value)
  const config = computed(() => getDestinoManual(destinoManual.value))

  const destinoNombre = ref("")
  const inicio = ref("")
  const fin = ref("")
  const pax = ref(2)
  const trm = ref(4200)
  const vuelos = ref(0)
  const traslados = ref(0)

  // Hoteles por grupo (estandar / gruposTravel / otrosUsd)
  const hotelesPorGrupo = ref({})

  // Combinados (MEX-CUN, VAR-HAV, PTY)
  const hotelesGrupoA = ref([nuevoHotel()])
  const hotelesGrupoB = ref({})

  // Tour (Turquía / Tailandia)
  const inicioPlan = ref("")
  const vueloNeto = ref(0)
  const nochesAdicionales = ref(0)

  // Crucero
  const fechaSalida = ref("")
  const cabinas = ref({
    interior: { sin: 0, con: 0 },
    exterior: { sin: 0, con: 0 },
    balcon: { sin: 0, con: 0 },
    suite: { sin: 0, con: 0 }
  })

  // San Andrés
  const habitaciones = ref([{ adultos: 2, ninos: 0 }])
  const hotelesDesayuno = ref([nuevoHotel()]) // netos Travel Compositor
  const hotelesTISeleccionados = ref([]) // nombres del tarifario

  // Costa (SMR / CTG)
  const planCosta = ref("Desayuno y Cena")
  const hotelesCosta = ref([{ nombre: "", tarifaAcomodacion: 0 }])

  // Reinicia estructuras al cambiar de destino
  watch(config, cfg => {
    hotelesPorGrupo.value = {}
    if (cfg?.grupos) {
      for (const g of cfg.grupos) {
        hotelesPorGrupo.value[g.key] = [nuevoHotel()]
      }
    }
    hotelesGrupoA.value = [nuevoHotel()]
    hotelesGrupoB.value = {}
    if (cfg?.grupoB) {
      for (const g of cfg.grupoB) {
        hotelesGrupoB.value[g.key] = [nuevoHotel()]
      }
    }
    hotelesTISeleccionados.value = cfg?.hotelesTI?.map(h => h.nombre) ?? []
    if (cfg?.destinoFijo) destinoNombre.value = cfg.destinoFijo
    if (cfg?.planes) planCosta.value = cfg.planes[0]
  }, { immediate: true })

  // --------------------------- HELPERS ---------------------------------

  const totalPaxSanAndres = computed(() =>
    habitaciones.value.reduce((a, h) => a + (h.adultos || 0) + (h.ninos || 0), 0)
  )

  const adultosSanAndres = computed(() =>
    habitaciones.value.reduce((a, h) => a + (h.adultos || 0), 0)
  )

  const ninosSanAndres = computed(() =>
    habitaciones.value.reduce((a, h) => a + (h.ninos || 0), 0)
  )

  function precioEstandar(valorHotel) {
    const fn = calc[config.value.calculo]
    return fn({
      vuelos: Number(vuelos.value),
      hotel: Number(valorHotel),
      traslados: Number(traslados.value),
      pax: Number(pax.value),
      trm: Number(trm.value),
      inicio: inicio.value,
      fin: fin.value
    })
  }

  function precioCombinado(valorA, valorB) {
    const base = {
      vuelos: Number(vuelos.value),
      pax: Number(pax.value),
      trm: Number(trm.value),
      inicio: inicio.value,
      fin: fin.value
    }
    switch (config.value.calculo) {
      case "tcMexCun":
        return calc.tcMexCun({ ...base, hotelMex: Number(valorA), hotelCun: Number(valorB) })
      case "tcVaraderoHabana":
        return calc.tcVaraderoHabana({ ...base, hotelHav: Number(valorA), hotelVar: Number(valorB) })
      case "tcPanamaCombinado":
        return calc.tcPanamaCombinado({ ...base, hotelCiudad: Number(valorA), hotelPlaya: Number(valorB) })
      default:
        return null
    }
  }

  const fmtPrecio = v => (v === REVISE_FECHAS ? v : dollar(v))

  // ------------------------ RESULTADOS ---------------------------------

  /**
   * Lista plana de resultados:
   * [{ grupo, plan, nombre, total, texto }]
   */
  const resultados = computed(() => {
    const cfg = config.value
    if (!cfg) return []
    const out = []

    if (["estandar", "gruposTravel", "otrosUsd"].includes(cfg.tipo)) {
      for (const g of cfg.grupos) {
        for (const h of hotelesPorGrupo.value[g.key] ?? []) {
          if (!h.nombre) continue
          let total
          if (cfg.tipo === "gruposTravel") {
            total = calc.gruposTravel({
              vuelos: Number(vuelos.value),
              netoHotel: Number(h.valor),
              inicio: inicio.value,
              fin: fin.value
            })
          } else if (cfg.tipo === "otrosUsd") {
            total = calc.tcOtrosUSD({
              vuelos: Number(vuelos.value),
              hotelUSD: Number(h.valor),
              pax: Number(pax.value),
              trm: Number(trm.value),
              inicio: inicio.value,
              fin: fin.value
            })
          } else {
            total = precioEstandar(h.valor)
          }
          out.push({ grupo: g.label, plan: g.plan, texto: g.texto, nombre: h.nombre, total })
        }
      }
    }

    if (cfg.tipo === "combinado") {
      for (const gB of cfg.grupoB) {
        for (const hB of hotelesGrupoB.value[gB.key] ?? []) {
          if (!hB.nombre) continue
          for (const hA of hotelesGrupoA.value) {
            if (!hA.nombre) continue
            const total = precioCombinado(hA.valor, hB.valor)
            const esCombinacionMexCun = cfg.calculo === "tcMexCun"
            const nombre = esCombinacionMexCun
              ? `${hA.nombre} con ${hB.nombre}`
              : `${hB.nombre} con ${hA.nombre}`
            out.push({ grupo: gB.label, plan: gB.plan, texto: cfg.texto, nombre, total })
          }
        }
      }
    }

    if (cfg.tipo === "sanAndres") {
      for (const h of hotelesDesayuno.value) {
        if (!h.nombre) continue
        out.push({
          grupo: "Hoteles solo desayuno",
          plan: "solo desayuno",
          texto: cfg.texto,
          nombre: h.nombre,
          total: calc.sanAndresSoloDesayuno({
            vuelos: Number(vuelos.value),
            netoHotel: Number(h.valor),
            inicio: inicio.value,
            fin: fin.value
          })
        })
      }
      for (const nombre of hotelesTISeleccionados.value) {
        const tarifa = cfg.hotelesTI.find(t => t.nombre === nombre)
        if (!tarifa) continue
        out.push({
          grupo: "Hoteles Todo Incluido",
          plan: "todo incluido",
          texto: cfg.texto,
          nombre,
          total: calc.sanAndresTodoIncluido({
            vuelos: Number(vuelos.value),
            habitaciones: habitaciones.value,
            tarifa,
            inicio: inicio.value,
            fin: fin.value
          })
        })
      }
    }

    if (cfg.tipo === "costa") {
      for (const h of hotelesCosta.value) {
        if (!h.nombre) continue
        out.push({
          grupo: `Hoteles ${planCosta.value.toLowerCase()}`,
          plan: planCosta.value.toLowerCase() === "todo incluido" ? "plan full" : planCosta.value.toLowerCase(),
          texto: cfg.texto,
          nombre: h.nombre,
          total: calc.costaConAlimentacion({
            vuelos: Number(vuelos.value),
            tarifaAcomodacion: Number(h.tarifaAcomodacion),
            pax: Number(pax.value),
            inicio: inicio.value,
            fin: fin.value
          })
        })
      }
    }

    return out
  })

  // Tour (Turquía/Tailandia) y Crucero tienen su propio resultado
  const resultadoTour = computed(() => {
    const cfg = config.value
    if (cfg?.tipo !== "tour" || !inicioPlan.value) return null
    const fn = calc[cfg.calculo]
    return fn({
      inicioPlan: inicioPlan.value,
      pax: Number(pax.value),
      trm: Number(trm.value),
      vueloNeto: Number(vueloNeto.value),
      nochesAdicionales: Number(nochesAdicionales.value)
    })
  })

  const resultadoCrucero = computed(() => {
    const cfg = config.value
    if (cfg?.tipo !== "crucero" || !fechaSalida.value) return null
    return calc.cruceroCtg({
      fechaSalida: fechaSalida.value,
      pax: Number(pax.value),
      cabinas: cabinas.value
    })
  })

  // ------------------------ MENSAJES WHATSAPP --------------------------

  const mensajePrincipal = computed(() => {
    const cfg = config.value
    if (!cfg) return ""

    // ---- TOUR (Turquía / Tailandia) ----
    if (cfg.tipo === "tour") {
      const r = resultadoTour.value
      if (!r) return ""
      const lineas = [
        `*COTIZACION VIAJE PARA ${pax.value} PERSONAS A ${cfg.label.toUpperCase()}*`,
        "",
        cfg.saludo,
        "",
        rangoFechas(r.ida, r.regreso),
        "",
        `*VALOR POR PERSONA:* ${dollar(r.porPersona)}`
      ]
      if (cfg.calculo === "tailandia") {
        lineas.push(`*Niño acompañado de 2 adultos:* ${dollar(r.porPersonaNino)}`)
      }
      lineas.push("", cfg.incluye)
      return lineas.join("\n")
    }

    // ---- CRUCERO ----
    if (cfg.tipo === "crucero") {
      const r = resultadoCrucero.value
      if (!r) return ""
      return [
        `*COTIZACION PARA ${pax.value} PERSONAS EN CRUCERO DESDE CARTAGENA*`,
        rangoFechas(fechaSalida.value, r.regreso),
        "",
        "DIAS 📅 8 DIAS",
        "PUERTO DE SALIDA ⚓ CARTAGENA",
        `NOMBRE NAVIERA ➡ ${r.naviera}`,
        "",
        `*Cabina ${r.tipoCabina} Sin Bebidas*`,
        `🚢Interior USD ${r.precios.interior.sin}`,
        `🚢Exterior USD ${r.precios.exterior.sin}`,
        `🚢Balcon USD ${r.precios.balcon.sin}`,
        `🚢Suite USD ${r.precios.suite.sin}`,
        "",
        `*Cabina ${r.tipoCabina} Con Bebidas*`,
        `🚢Interior USD ${r.precios.interior.con}`,
        `🚢Exterior USD ${r.precios.exterior.con}`,
        `🚢Balcon USD ${r.precios.balcon.con}`,
        `🚢Suite USD ${r.precios.suite.con}`,
        "",
        "Los precios dados anteriormente son por el total de personas que viajan 💎💎💎 (Es el precio total que pagarias por todos en 1 cabina)"
      ].join("\n")
    }

    // ---- COTIZACIONES CON HOTELES ----
    if (!resultados.value.length) return ""

    const titulo =
      cfg.tipo === "sanAndres"
        ? `*COTIZACION PARA ${adultosSanAndres.value} ADULTOS Y ${ninosSanAndres.value} NIÑOS EN ${(destinoNombre.value || "San Andres").toUpperCase()}*`
        : `*COTIZACION PARA ${pax.value} PERSONAS EN ${(destinoNombre.value || "").toUpperCase()}*`

    const lineas = [titulo, rangoFechas(inicio.value, fin.value), ""]

    // Agrupar manteniendo el orden
    const grupos = [...new Set(resultados.value.map(r => r.grupo))]
    for (const g of grupos) {
      lineas.push(`*${g}:*`)
      for (const r of resultados.value.filter(x => x.grupo === g)) {
        const precio = cfg.tipo === "otrosUsd" && r.total !== REVISE_FECHAS
          ? `USD ${r.total}`
          : fmtPrecio(r.total)
        lineas.push(`🏣${proper(r.nombre)} ${precio}`)
      }
      lineas.push("")
    }

    lineas.push(NOTA_PRECIO_TOTAL)
    return lineas.join("\n")
  })

  const mensajeSegundo = computed(() => {
    const cfg = config.value
    if (!cfg || cfg.tipo === "tour" || cfg.tipo === "crucero") {
      return cfg?.tipo === "crucero" ? cfg.texto : ""
    }
    if (!resultados.value.length) return ""

    const divisor =
      cfg.tipo === "sanAndres" ? totalPaxSanAndres.value : Number(pax.value)

    // Primer hotel de cada grupo (como el Excel: primer desayuno + primer TI)
    const primeros = []
    const vistos = new Set()
    for (const r of resultados.value) {
      if (vistos.has(r.grupo) || r.total === REVISE_FECHAS) continue
      vistos.add(r.grupo)
      primeros.push(r)
    }
    if (!primeros.length) return ""

    const lineasPP = primeros.map(r => {
      const pp = r.total / divisor
      const valor = cfg.tipo === "otrosUsd" ? `${dollar(pp)} USD` : dollar(pp)
      const plan = r.plan ? ` _(${r.plan})_` : ""
      return `*${proper(r.nombre)}*: ${valor} por persona${plan}`
    })

    const texto = primeros[primeros.length - 1].texto || cfg.texto || ""

    return [
      `🤓 *ES DECIR...* 🧠⚡\n${lineasPP.join(" \n")}  🥳😍🥳`,
      "",
      texto
    ].join("\n")
  })

  // ------------------------ ACCIONES -----------------------------------

  function agregarHotel(lista) {
    lista.push(nuevoHotel())
  }

  function eliminarHotel(lista, index) {
    if (lista.length > 1) lista.splice(index, 1)
  }

  function agregarHabitacion() {
    if (habitaciones.value.length < 10) {
      habitaciones.value.push({ adultos: 2, ninos: 0 })
    }
  }

  function eliminarHabitacion(index) {
    if (habitaciones.value.length > 1) habitaciones.value.splice(index, 1)
  }

  const validacionesHabitaciones = computed(() =>
    habitaciones.value.map(h => calc.validarHabitacionSanAndres(h))
  )

  return {
    // config
    destinosManual,
    destinoManual,
    config,

    // state
    destinoNombre,
    inicio,
    fin,
    pax,
    trm,
    vuelos,
    traslados,
    hotelesPorGrupo,
    hotelesGrupoA,
    hotelesGrupoB,
    inicioPlan,
    vueloNeto,
    nochesAdicionales,
    fechaSalida,
    cabinas,
    habitaciones,
    hotelesDesayuno,
    hotelesTISeleccionados,
    planCosta,
    hotelesCosta,

    // computeds
    resultados,
    resultadoTour,
    resultadoCrucero,
    mensajePrincipal,
    mensajeSegundo,
    totalPaxSanAndres,
    validacionesHabitaciones,

    // actions
    agregarHotel,
    eliminarHotel,
    agregarHabitacion,
    eliminarHabitacion
  }
}
